/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, { useCallback } from "react";
import { useNavigate } from "react-router";

import type { Locale, Site } from "~/config/sites";
import { type CountryType } from "~/lib/countries";
import { useRequestInfo } from "~/lib/utils/request-info";
import { invariant } from "~/lib/utils/utils";
import { useSetCountry } from "~/routes/resources/country";

export { type Locale, type Site };

export type buildUrlType = (url: string | URL, locale?: Locale) => string;

export type SiteContextType = {
  site: Site;
  locale: Locale;
  country: CountryType;
  setLocale: (locale: Locale) => void;
  setCountry: (countryCode: string) => void;
  buildUrl: buildUrlType;
};

export const SiteContext = React.createContext<SiteContextType>(
  undefined as any,
);
/**
 * This is the global state for the multiples sites and locales supported in the App.
 *
 * To use the context simply import them into the component requiring context
 * like the below example:
 *
 * import React, {useContext} from 'react'
 * import {MultiSiteContext} from './contexts'
 *
 * export const RootCurrencyLabel = () => {
 *    const {site,locale,urlTemplate} = useContext(MultiSiteContext)
 *    return <div>{site} {locale}</div>
 * }
 *
 * Alternatively you can use the hook provided by us:
 *
 * import {useMultiSite} from './hooks'
 *
 * const {site, locale, buildUrl} = useMultiSite()
 * @type {React.Context<unknown>}
 */
export const SiteProvider = ({
  site,
  locale,
  buildUrl,
  children,
}: {
  site: Site;
  locale: Locale;
  buildUrl: buildUrlType;
  children: React.ReactNode;
}) => {
  const { setCountry } = useSetCountry();
  const { country } = useRequestInfo();
  const navigate = useNavigate();
  const setLocale = useCallback(
    (locale: Locale) => {
      navigate(buildUrl(`/`, locale));
    },
    [navigate, buildUrl],
  );

  invariant(
    country,
    "Country not found for locale: " + locale.alias || locale.id,
  );

  return (
    <SiteContext.Provider
      value={{ site, locale, country, setLocale, setCountry, buildUrl }}
    >
      {children}
    </SiteContext.Provider>
  );
};

export const useSite = () => {
  const context = React.useContext(SiteContext);
  if (!context) {
    throw new Error("useMultiSite must be used within a MultiSiteProvider");
  }
  return context;
};

export const useURL = () => {
  const context = React.useContext(SiteContext);
  if (!context) {
    throw new Error("useURL must be used within a MultiSiteProvider");
  }
  return context.buildUrl;
};
